import type { PresetDefinition } from '@wix/wix-data-client-common'
import { EDITOR_TYPE } from '@wix/wix-data-client-common'
import type { EditorSDK } from '@wix/wix-data-client-common-standalone'
import { nonNullable } from '@wix/wix-data-client-common-standalone'
import * as classicPresets from './classic/definitionLookup'
import * as studioPresets from './studio/definitionLookup'
import * as editor3Presets from './editor3/definitionLookup'
import { removeOrphanedGridRows } from './removeOrphanedGridRows'

const definitionsByEditorType: Partial<Record<EDITOR_TYPE, any>> = {
  [EDITOR_TYPE.CLASSIC]: classicPresets,
  [EDITOR_TYPE.STUDIO]: studioPresets,
  [EDITOR_TYPE.EDITOR3]: editor3Presets,
}

const getDeferredDefinition = (
  presetId: string,
  editorType: EDITOR_TYPE,
): (() => Promise<{ default: PresetDefinition }>) => {
  const definitions = definitionsByEditorType[editorType] || classicPresets
  return definitions[presetId]
}

const getUnusedRootIds = async (
  referencedIds: string[],
  editorSdkProxy: EditorSDK,
): Promise<string[]> => {
  const unusedIds = await Promise.all(
    referencedIds.map(id =>
      editorSdkProxy.components
        .getById({ id })
        .then(res => (res === null ? id : null)),
    ),
  )
  return unusedIds.filter(nonNullable)
}

const getReferenceRootIds = (definition: PresetDefinition): string[] => {
  const ids = new Set<string>()
  for (const page of definition.pages) {
    for (const component of page.definition.components) {
      if (
        component.componentType === 'wysiwyg.viewer.components.RefComponent'
      ) {
        ids.add(component.data?.rootCompId as string)
      }
    }
  }
  return Array.from(ids)
}

const removeNonExistantRefComponents = async (
  definition: PresetDefinition,
  editorSdkProxy: EditorSDK,
): Promise<PresetDefinition> => {
  const referenceRootIds = getReferenceRootIds(definition)
  const unusedRootIds = await getUnusedRootIds(referenceRootIds, editorSdkProxy)
  return {
    ...definition,
    pages: definition.pages.map(page => {
      const components = page.definition.components.filter(component => {
        return !(
          component.componentType ===
            'wysiwyg.viewer.components.RefComponent' &&
          unusedRootIds.includes(component.data?.rootCompId as string)
        )
      })
      const hasRemovedComponents =
        components.length !== page.definition.components.length
      const pageDefinition = { ...page.definition, components }
      return {
        ...page,
        definition: hasRemovedComponents
          ? removeOrphanedGridRows(pageDefinition)
          : pageDefinition,
      }
    }),
  }
}

export const getPresetDefinitionById = async (
  presetId: string,
  editorType: EDITOR_TYPE,
  editorSdkProxy: EditorSDK,
): Promise<PresetDefinition> => {
  const deferred = getDeferredDefinition(presetId, editorType)
  const { default: definition } = await deferred()
  return removeNonExistantRefComponents(definition, editorSdkProxy)
}

export const presetExists = (presetId: string, editorType: EDITOR_TYPE) => {
  const preset = getDeferredDefinition(presetId, editorType)
  return Boolean(preset)
}
